/**
 * WizardHeader — Back button + "TruePrice" brand + "Price Your Work" title + progress bar.
 *
 * Progress bar replaces the numeric "x/8" indicator.
 * Segmented bar with 8 segments, each fills teal when the step is reached.
 * Review mode (activeStep === totalSteps) shows all segments filled.
 */

import { T } from '@/styles/tokens';

interface WizardHeaderProps {
  activeStep: number;
  totalSteps: number;
  onBack: () => void;
  canGoBack: boolean;
}

export function WizardHeader({ activeStep, totalSteps, onBack, canGoBack }: WizardHeaderProps) {
  // Clamp progress: review mode (activeStep >= totalSteps) shows full
  const filledCount = Math.min(activeStep + 1, totalSteps);

  return (
    <header style={{ padding: '16px 20px 0' }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        marginBottom: 12,
      }}>
        <button
          onClick={onBack}
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            border: `1.5px solid ${T.border}`,
            background: T.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: canGoBack ? 'pointer' : 'default',
            color: T.text,
            padding: 0,
            opacity: canGoBack ? 1 : 0.35,
            transition: 'opacity 0.2s ease',
            boxShadow: canGoBack ? '0 1px 3px rgba(0,0,0,0.06)' : 'none',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M10 13L5 8l5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11,
            fontWeight: 700,
            color: T.primary,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
          }}>
            TruePrice
          </div>
          <h1 style={{
            fontSize: 22,
            fontWeight: 800,
            color: T.text,
            margin: 0,
            letterSpacing: '-0.02em',
            lineHeight: 1.2,
          }}>
            Price Your Work
          </h1>
        </div>
      </div>

      {/* Segmented progress bar */}
      <div style={{
        display: 'flex',
        gap: 3,
        paddingBottom: 12,
      }}>
        {Array.from({ length: totalSteps }, (_, i) => {
          const isFilled = i < filledCount;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 1.5,
                backgroundColor: isFilled ? T.primary : T.border,
                transition: 'background-color 0.3s ease',
              }}
            />
          );
        })}
      </div>
    </header>
  );
}
